import {
  LayoutDashboard,
  User,
  ChevronRight,
  FileUser,
  Server,
  Store,
  CircleHelp,
  Inbox,
  FileClock,
  Presentation,
  Calendar,
  History,
  SearchX,
  TriangleAlert,
  ChartNoAxesCombined,
  ReceiptText,
  Folder,
  MessageSquareText,
  Send,
  Settings as LucideSettings,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { Icon as PhosphorIcon, IconProps as PhosphorIconProps } from '@phosphor-icons/react';
import {
  SquaresFour,
  Users,
  ListBullets,
  Funnel,
  Prohibit,
  Megaphone,
  FileText,
  Gear,
  SlidersHorizontal,
  UsersThree,
  House,
  Question,
  Clock,
  ClipboardText,
  Airplane,
  CalendarDots,
  FirstAidKit,
  Wrench,
  Confetti,
  Timer,
  ListChecks,
  ClockCountdown,
  ChartLineUp,
  NotePencil,
  ForkKnife,
  IdentificationCard,
  Handshake,
  ChartBar,
  Headset,
  Buildings,
  IdentificationBadge,
  UserGear,
  Kanban,
  Target,
  MapPin,
  ShieldCheck,
  Globe,
  Certificate,
  Laptop,
  Cardholder,
  Umbrella,
  CurrencyCircleDollar,
  Lightbulb,
} from '@phosphor-icons/react';
import {
  SidebarHrmIcon,
  SidebarContactsIcon,
  SidebarDashboardIcon,
  SidebarSettingsIcon,
  SidebarTemplatesIcon,
} from '../sidebar-nav-icons/sidebar-nav-icons';

/**
 * MenuIcon Component
 *
 * Maps string names to Lucide or Phosphor icons for consistent menu usage.
 * Sidebar nav prefers Phosphor; other callers may still use Lucide names.
 */

const phosphorAsLucideCompatible = (Icon: PhosphorIcon) => {
  const Wrapped = ({
    size = 24,
    className,
    color,
    weight = 'regular',
    ...rest
  }: LucideProps & { weight?: PhosphorIconProps['weight'] }) => {
    const resolvedSize = typeof size === 'number' ? size : Number(size) || 24;
    const phosphorProps: PhosphorIconProps = {
      size: resolvedSize,
      weight,
      className,
      color: typeof color === 'string' ? color : undefined,
    };
    // Drop Lucide-only props (e.g. strokeWidth) so they are not forwarded.
    void rest;
    return <Icon {...phosphorProps} />;
  };
  Wrapped.displayName = `Phosphor(${Icon.displayName ?? 'Icon'})`;
  return Wrapped;
};

const iconMap = {
  LayoutDashboard,
  SidebarDashboard: SidebarDashboardIcon,
  SidebarHrm: SidebarHrmIcon,
  SidebarContacts: SidebarContactsIcon,
  SidebarTemplates: SidebarTemplatesIcon,
  SidebarSettings: SidebarSettingsIcon,
  User,
  ChevronRight,
  FileUser,
  Users: phosphorAsLucideCompatible(Users),
  SquaresFour: phosphorAsLucideCompatible(SquaresFour),
  ListBullets: phosphorAsLucideCompatible(ListBullets),
  Funnel: phosphorAsLucideCompatible(Funnel),
  Prohibit: phosphorAsLucideCompatible(Prohibit),
  Megaphone: phosphorAsLucideCompatible(Megaphone),
  FileText: phosphorAsLucideCompatible(FileText),
  Gear: phosphorAsLucideCompatible(Gear),
  SlidersHorizontal: phosphorAsLucideCompatible(SlidersHorizontal),
  UsersThree: phosphorAsLucideCompatible(UsersThree),
  House: phosphorAsLucideCompatible(House),
  Question: phosphorAsLucideCompatible(Question),
  Clock: phosphorAsLucideCompatible(Clock),
  ClipboardText: phosphorAsLucideCompatible(ClipboardText),
  Airplane: phosphorAsLucideCompatible(Airplane),
  CalendarDots: phosphorAsLucideCompatible(CalendarDots),
  FirstAidKit: phosphorAsLucideCompatible(FirstAidKit),
  Wrench: phosphorAsLucideCompatible(Wrench),
  Confetti: phosphorAsLucideCompatible(Confetti),
  Timer: phosphorAsLucideCompatible(Timer),
  ListChecks: phosphorAsLucideCompatible(ListChecks),
  ClockCountdown: phosphorAsLucideCompatible(ClockCountdown),
  ChartLineUp: phosphorAsLucideCompatible(ChartLineUp),
  NotePencil: phosphorAsLucideCompatible(NotePencil),
  ForkKnife: phosphorAsLucideCompatible(ForkKnife),
  IdentificationCard: phosphorAsLucideCompatible(IdentificationCard),
  Handshake: phosphorAsLucideCompatible(Handshake),
  ChartBar: phosphorAsLucideCompatible(ChartBar),
  Headset: phosphorAsLucideCompatible(Headset),
  Buildings: phosphorAsLucideCompatible(Buildings),
  IdentificationBadge: phosphorAsLucideCompatible(IdentificationBadge),
  UserGear: phosphorAsLucideCompatible(UserGear),
  Kanban: phosphorAsLucideCompatible(Kanban),
  Target: phosphorAsLucideCompatible(Target),
  MapPin: phosphorAsLucideCompatible(MapPin),
  ShieldCheck: phosphorAsLucideCompatible(ShieldCheck),
  Globe: phosphorAsLucideCompatible(Globe),
  Certificate: phosphorAsLucideCompatible(Certificate),
  Laptop: phosphorAsLucideCompatible(Laptop),
  Cardholder: phosphorAsLucideCompatible(Cardholder),
  Umbrella: phosphorAsLucideCompatible(Umbrella),
  CurrencyCircleDollar: phosphorAsLucideCompatible(CurrencyCircleDollar),
  Lightbulb: phosphorAsLucideCompatible(Lightbulb),
  Server,
  Store,
  CircleHelp,
  Inbox,
  FileClock,
  Presentation,
  Calendar,
  History,
  SearchX,
  TriangleAlert,
  ChartNoAxesCombined,
  ReceiptText,
  Folder,
  MessageSquareText,
  Send,
  Settings: LucideSettings,
} as const;

export type MenuIconName = keyof typeof iconMap;

interface MenuIconProps extends Omit<LucideProps, 'ref'> {
  name: MenuIconName;
  weight?: PhosphorIconProps['weight'];
}

export const MenuIcon = ({
  name,
  size = 24,
  strokeWidth,
  className,
  weight,
  ...props
}: Readonly<MenuIconProps>) => {
  const Icon = iconMap[name];
  const resolvedSize = typeof size === 'number' ? size : Number(size) || 24;

  return (
    <Icon
      size={resolvedSize}
      strokeWidth={strokeWidth}
      className={className}
      weight={weight}
      {...props}
    />
  );
};
